import { Box, Typography, IconButton, Button, Tooltip, useTheme, alpha, Rating } from '@mui/material'
import { motion } from 'framer-motion'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { DisplayCurrency } from 'components'

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)

const COLOR_MAP = {
  r: '#EF4444', b: '#3B82F6', g: '#22C55E', y: '#EAB308', o: '#F97316', p: '#A855F7',
  k: '#111827', w: '#F9FAFB',
  red: '#EF4444', blue: '#3B82F6', green: '#22C55E', yellow: '#EAB308',
  orange: '#F97316', purple: '#A855F7', pink: '#EC4899', black: '#111827',
  white: '#F9FAFB', gray: '#9CA3AF', grey: '#9CA3AF', navy: '#1E3A5F',
  teal: '#0D9488', brown: '#92400E', gold: '#F59E0B', silver: '#CBD5E1',
  coral: '#F87171', indigo: '#6366F1', rose: '#FB7185', emerald: '#10B981',
  violet: '#7C3AED', maroon: '#7F1D1D', cyan: '#06B6D4', lime: '#84CC16',
}

const ColorDot = ({ color }) => {
  const name = typeof color === 'string' ? color : color.name
  const hexDb = typeof color === 'string' ? null : color.color_code
  const css = hexDb || COLOR_MAP[name?.toLowerCase()] ||
    (/^#[0-9a-f]{3,8}$/i.test(name ?? '') ? name : null)
  const isLight = css && /^#[fFeEdDcC]{3,6}/.test(css)

  return (
    <Tooltip title={name} placement="top" arrow>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: css || 'primary.main',
          border: (theme) => `1.5px solid ${isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.25)}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          cursor: 'default',
          transition: 'transform 0.15s',
          '&:hover': { transform: 'scale(1.35)' },
          ...(!css && { background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` }),
        }}
      />
    </Tooltip>
  )
}

const PremiumProductCard = ({
  product,
  index = 0,
  layout = 'grid',
  quickView = false,
  rankBadge,
  newBadge = false,
  featuredBadge = Boolean(product?.isFeatured ?? product?.is_featured),
  onNavigate,
  onQuickView,
  onAddToCart,
  onRemoveFromCart,
  onToggleWishlist,
  inCart = false,
  cartQty = 0,
  isFav = false,
  finalPrice,
}) => {
  const t = useTheme()
  const isDark = t.palette.mode === 'dark'
  const isList = layout === 'list'

  const hasVariants = !!(product?.hasVariants ?? product?.has_variants)
  const discPct = Number(product.discountPercentage || 0)
  const variantColors = (product.variantColors || []).slice(0, 5)
  const variantSizes = (product.variantSizes || []).slice(0, 4)
  const ratingValue = Number(product?.rating || 0)
  const reviewCount = Number(product?.reviewCount || 0)
  const secondaryImage = product?.secondaryImage || product?.secondary_image || product?.hover_image || null
  const metaLabel = product?.brand || product?.category_name || product?.category || ''

  const minP = product.variantMinPrice
  const maxP = product.variantMaxPrice
  const priceRangeLabel = hasVariants && minP != null
    ? (minP === maxP ? fmt(minP) : `${fmt(minP)} – ${fmt(maxP)}`)
    : null

  const rankColor = rankBadge === '#1' ? '#F59E0B'
    : rankBadge === '#2' ? '#94A3B8'
    : rankBadge === '#3' ? '#B45309'
    : t.palette.primary.main

  const actionButtonSx = {
    height: 40,
    borderRadius: '14px',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.8rem',
    px: 1.6,
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.055, duration: 0.36, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ height: '100%' }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '24px',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: isList ? { xs: 'column', sm: 'row' } : 'column',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isDark ? alpha(t.palette.common.white, 0.08) : alpha(t.palette.common.black, 0.06),
          background: isDark
            ? `linear-gradient(180deg, ${alpha(t.palette.background.paper, 0.94)} 0%, ${alpha(t.palette.background.elevated || t.palette.background.paper, 0.98)} 100%)`
            : `linear-gradient(180deg, ${t.palette.background.paper} 0%, ${alpha(t.palette.grey[50], 0.7)} 100%)`,
          boxShadow: isDark ? '0 18px 48px rgba(0,0,0,0.42)' : '0 16px 40px rgba(15,23,42,0.08)',
          transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: isDark
              ? `0 20px 54px rgba(0,0,0,0.52), 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`
              : `0 20px 48px rgba(15,23,42,0.14), 0 0 0 1px ${alpha(t.palette.primary.main, 0.12)}`,
            borderColor: alpha(t.palette.primary.main, isDark ? 0.22 : 0.14),
          },
          '&:hover .pm-img': { transform: 'scale(1.06)' },
          '&:hover .pm-img-secondary': { opacity: secondaryImage ? 1 : 0 },
          '&:hover .pm-fav': { opacity: 1, transform: 'translateY(0) scale(1)' },
          '&:hover .pm-quick': { opacity: 1, transform: 'translateY(0)' },
        }}
      >
        <Box
          onClick={onNavigate}
          sx={{
            position: 'relative',
            width: isList ? { xs: '100%', sm: '42%' } : '100%',
            minWidth: isList ? { sm: 280 } : '100%',
            pt: isList ? { xs: '76%', sm: 0 } : '76%',
            minHeight: isList ? { sm: 340 } : 0,
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
            bgcolor: isDark
              ? alpha(t.palette.background.elevated || t.palette.background.paper, 0.6)
              : t.palette.grey[50],
          }}
        >
          <Box
            component="img"
            src={product.thumbnail || '/images/picture-loading-failed.svg'}
            alt={product.title}
            className="pm-img"
            onError={(e) => { e.target.src = '/images/picture-loading-failed.svg'; e.target.onerror = null }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.42s cubic-bezier(.25,.46,.45,.94)',
            }}
          />

          {secondaryImage && secondaryImage !== product.thumbnail && (
            <Box
              component="img"
              src={secondaryImage}
              alt={`${product.title} alternate view`}
              className="pm-img-secondary"
              onError={(e) => { e.target.style.display = 'none' }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0,
                transition: 'opacity 0.35s ease',
              }}
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 54,
              background: `linear-gradient(to top, ${t.palette.background.paper}, transparent)`,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.6,
            }}
          >
            {rankBadge && (
              <Box
                sx={{
                  bgcolor: rankColor,
                  color: '#fff',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '0.6rem',
                  boxShadow: `0 2px 8px ${alpha(rankColor, 0.55)}`,
                }}
              >
                {rankBadge}
              </Box>
            )}
            {newBadge && (
              <Box
                sx={{
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                  borderRadius: '6px',
                  px: 0.8,
                  py: 0.2,
                  fontSize: '0.58rem',
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.3,
                  boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.success.main, 0.5)}`,
                }}
              >
                <AutoAwesomeRoundedIcon sx={{ fontSize: 9 }} />
                NEW
              </Box>
            )}
            {featuredBadge && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                  color: '#241400',
                  borderRadius: '999px',
                  px: 1,
                  py: 0.28,
                  fontSize: '0.58rem',
                  fontWeight: 900,
                  letterSpacing: 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.35,
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
                }}
              >
                <AutoAwesomeRoundedIcon sx={{ fontSize: 9 }} />
                FEATURED
              </Box>
            )}
            {discPct > 0 && (
              <Box
                sx={{
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  borderRadius: '999px',
                  px: 1,
                  py: 0.28,
                  fontSize: '0.62rem',
                  fontWeight: 900,
                  boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.error.main, 0.45)}`,
                }}
              >
                -{discPct.toFixed(0)}%
              </Box>
            )}
          </Box>

          <IconButton
            className="pm-fav"
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggleWishlist() }}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 4,
              opacity: { xs: 1, md: 0.9 },
              transform: { xs: 'translateY(0) scale(1)', md: 'translateY(-6px) scale(0.88)' },
              transition: 'opacity 0.22s, transform 0.22s',
              bgcolor: isDark ? alpha(t.palette.background.paper, 0.82) : alpha('#fff', 0.9),
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: 'divider',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: isFav ? alpha(t.palette.error.main, 0.15) : alpha(t.palette.primary.main, 0.1),
              },
            }}
          >
            {isFav
              ? <FavoriteRoundedIcon sx={{ fontSize: 17, color: 'error.main' }} />
              : <FavoriteBorderRoundedIcon sx={{ fontSize: 17, color: 'text.secondary' }} />}
          </IconButton>

          {quickView && (
            <Button
              className="pm-quick"
              variant="contained"
              startIcon={<VisibilityRoundedIcon sx={{ fontSize: '16px !important' }} />}
              onClick={(e) => {
                e.stopPropagation()
                onQuickView?.()
              }}
              sx={{
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 12,
                zIndex: 4,
                borderRadius: '999px',
                py: 1,
                opacity: { xs: 1, md: 0 },
                transform: { xs: 'translateY(0)', md: 'translateY(10px)' },
                bgcolor: alpha(t.palette.common.black, isDark ? 0.44 : 0.78),
                color: t.palette.common.white,
                backdropFilter: 'blur(16px)',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: alpha(t.palette.primary.main, 0.92),
                  boxShadow: 'none',
                },
              }}
            >
              Quick View
            </Button>
          )}
        </Box>

        <Box
          sx={{
            p: isList ? { xs: 2, sm: 2.5 } : { xs: 1.8, sm: 2.1 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: isList ? 240 : 180,
          }}
        >
          {metaLabel && (
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 0.75,
              }}
            >
              {metaLabel}
            </Typography>
          )}

          <Typography
            onClick={onNavigate}
            sx={{
              fontSize: isList ? { xs: '0.95rem', sm: '1.05rem' } : { xs: '0.82rem', sm: '0.92rem' },
              fontWeight: 700,
              color: 'text.primary',
              cursor: 'pointer',
              lineHeight: 1.35,
              mb: 0.7,
              transition: 'color 0.15s',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: isList ? '2.7em' : '2.5em',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {product.title}
          </Typography>

          {(ratingValue > 0 || reviewCount > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.9 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                <StarRoundedIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
                  {ratingValue > 0 ? ratingValue.toFixed(1) : 'New'}
                </Typography>
              </Box>
              {reviewCount > 0 && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  ({reviewCount.toLocaleString('en-IN')} reviews)
                </Typography>
              )}
            </Box>
          )}

          {ratingValue > 0 && reviewCount === 0 && (
            <Rating
              value={ratingValue}
              precision={0.5}
              readOnly
              size="small"
              sx={{ mb: 0.9, '& .MuiRating-iconFilled': { color: 'warning.main' } }}
            />
          )}

          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.9, mb: 1.1 }}>
            {hasVariants && priceRangeLabel ? (
              <Typography
                sx={{
                  fontSize: isList ? { xs: '1.02rem', sm: '1.18rem' } : { xs: '0.92rem', sm: '1rem' },
                  fontWeight: 800,
                  color: 'primary.main',
                  lineHeight: 1,
                }}
              >
                {priceRangeLabel}
              </Typography>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: isList ? { xs: '1.02rem', sm: '1.18rem' } : { xs: '0.92rem', sm: '1rem' },
                    fontWeight: 800,
                    color: 'primary.main',
                    lineHeight: 1,
                  }}
                >
                  <DisplayCurrency number={finalPrice} />
                </Typography>
                {discPct > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: '0.76rem',
                        fontWeight: 500,
                        lineHeight: 1,
                        color: 'text.disabled',
                        textDecoration: 'line-through',
                      }}
                    >
                      <DisplayCurrency number={product.originalPrice} />
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.74rem',
                        fontWeight: 800,
                        color: 'error.main',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Save {discPct}%
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>

          <Box sx={{ minHeight: 28, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.65, mb: 1.2 }}>
            {variantColors.map((c) => (
              <ColorDot key={typeof c === 'string' ? c : c.name} color={c} />
            ))}
            {variantColors.length > 0 && (product.variantColors?.length || 0) > 5 && (
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' }}>
                +{product.variantColors.length - 5}
              </Typography>
            )}
            {variantColors.length > 0 && variantSizes.length > 0 && (
              <Box sx={{ width: 1, height: 10, bgcolor: 'divider', mx: 0.2 }} />
            )}
            {variantSizes.map((s) => (
              <Box
                key={s}
                sx={{
                  px: 0.7,
                  borderRadius: '999px',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: '0.58rem',
                  fontWeight: 800,
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
                  letterSpacing: 0.25,
                }}
              >
                {s}
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: isList ? 'wrap' : 'nowrap' }}>
            {hasVariants ? (
              <Button
                onClick={onNavigate}
                endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: '10px !important' }} />}
                sx={{
                  ...actionButtonSx,
                  minWidth: isList ? 180 : 'auto',
                  flex: isList ? '0 0 auto' : 1,
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                  transition: 'all 0.18s',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Select Options
              </Button>
            ) : inCart ? (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  height: 40,
                  flex: isList ? '0 0 auto' : 1,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                  borderRadius: '14px',
                  px: 0.7,
                }}
              >
                <IconButton
                  size="small"
                  onClick={onRemoveFromCart}
                  sx={{
                    color: 'primary.main',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15) },
                  }}
                >
                  <RemoveRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    color: 'text.primary',
                    minWidth: 30,
                    textAlign: 'center',
                  }}
                >
                  {cartQty}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onAddToCart}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    width: 28,
                    height: 28,
                    borderRadius: '10px',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <AddRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="contained"
                startIcon={<ShoppingCartOutlinedIcon sx={{ fontSize: '17px !important' }} />}
                onClick={onAddToCart}
                sx={{
                  ...actionButtonSx,
                  flex: isList ? '0 0 auto' : 1,
                  minWidth: isList ? 180 : 'auto',
                  boxShadow: (theme) => `0 10px 24px ${alpha(theme.palette.primary.main, 0.28)}`,
                  '&:hover': {
                    boxShadow: (theme) => `0 14px 28px ${alpha(theme.palette.primary.main, 0.36)}`,
                  },
                }}
              >
                Add to Cart
              </Button>
            )}

            {!hasVariants && quickView && isList && (
              <Tooltip title="Quick view" placement="top">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    onQuickView?.()
                  }}
                  size="small"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '14px',
                    color: 'text.primary',
                    bgcolor: alpha(t.palette.text.primary, isDark ? 0.08 : 0.05),
                    '&:hover': {
                      bgcolor: alpha(t.palette.primary.main, 0.12),
                      color: 'primary.main',
                    },
                  }}
                >
                  <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </motion.div>
  )
}

export const PremiumProductCardSkeleton = () => (
  <Box
    sx={{
      borderRadius: '24px',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 4px 24px rgba(0,0,0,0.45)'
        : '0 2px 12px rgba(0,0,0,0.07)',
    }}
  >
    <Box
      sx={{
        pt: '76%',
        position: 'relative',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.elevated || '#161E35' : theme.palette.grey[100],
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          '@keyframes pm-shimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' },
          },
          background: (theme) => `linear-gradient(90deg,
            transparent 25%,
            ${alpha(theme.palette.text.primary, 0.05)} 50%,
            transparent 75%)`,
          backgroundSize: '200% 100%',
          animation: 'pm-shimmer 1.6s linear infinite',
        }}
      />
    </Box>
    <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {[['38%', 12], ['78%', 18], ['55%', 12], ['45%', 18], ['100%', 40]].map(([w, h], i) => (
        <Box
          key={i}
          sx={{
            width: w,
            height: h,
            borderRadius: '8px',
            bgcolor: (theme) => alpha(theme.palette.text.primary, i === 4 ? 0.07 : 0.05),
          }}
        />
      ))}
    </Box>
  </Box>
)

export default PremiumProductCard
